import React, { useEffect } from "react";
import { Box, Grid, InputAdornment, MenuItem, Typography } from "@mui/material";
import ButtonComp from "../../../../components/Button";
import Input from "../../../../components/Input";
import theme from "../../../../theme";
import { useState } from "react";
import { addVehicle, getFleetBranches } from "../../../../services/dashboard";
import { useToast } from "../../../../hooks/useToast";
import { useAllManufacturer } from "../../../../hooks/useAllManufacturer";
import { useAllModels } from "../../../../hooks/useAllModals";

export default function VehicleDetails({
  setIsLoading,
  formData,
  setFormData,
  setStep,
}: any) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fleetBranches, setFleetBranches] = useState<any[]>([]);
  const { manufacturer } = useAllManufacturer();
  const { models, fetchModels } = useAllModels();

  const { showInfoToast } = useToast();

  useEffect(() => {
    const loadBranches = async () => {
      const response = await getFleetBranches();
      if (response?.status === 200 && Array.isArray(response.data)) {
        setFleetBranches(
          response.data
            .sort((a: any, b: any) =>
              String(a.branchName || "").localeCompare(String(b.branchName || "")),
            ),
        );
      }
    };

    void loadBranches();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === "manufacturerId") {
      fetchModels(value);
      setFormData((prev: any) => ({
        ...prev,
        manufacturerId: value,
        modelId: "",
      }));
      setErrors((prev: any) => ({
        ...prev,
        [name]: "",
        modelId: "",
      }));
      return;
    }

    if (name === "branchId") {
      const selectedBranch = fleetBranches.find(
        (branch: any) => String(branch.id) === String(value),
      );
      setFormData((prev: any) => ({
        ...prev,
        branchId: value,
        externalBranchId: "",
        branchLocation: selectedBranch?.branchName || "",
      }));
      setErrors((prev: any) => ({
        ...prev,
        branchId: "",
      }));
      return;
    }

    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev: any) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleValidate = () => {
    let newErrors: any = {};

    if (!formData.vehicleRegistrationNumber)
      newErrors.vehicleRegistrationNumber = "Registration number is required";
    if (!formData.vehicleType)
      newErrors.vehicleType = "Vehicle type is required";
    if (!formData.branchId)
      newErrors.branchId = "Branch is required";
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!handleValidate()) return;
    setIsLoading(true);
    try {
      const payload = {
        vehicleRegistrationNumber: formData.vehicleRegistrationNumber,
        vehicleType: formData.vehicleType,
        branchId: formData.branchId || undefined,
        branchLocation: formData.branchLocation || undefined,
        manufacturerId: formData.manufacturerId || undefined,
        modelId: formData.modelId || undefined,
        vehicleColor: formData.vehicleColor || undefined,
        fleetCategory: formData.fleetCategory || undefined,
        dayDelivery: formData.dayDelivery || undefined,
        materialSource: formData.materialSource || undefined,
        currentMileage:
          formData.currentMileage === "" ? undefined : Number(formData.currentMileage),
        requiredLicenseCode: formData.requiredLicenseCode || undefined,
        requiresPdp: Boolean(formData.requiresPdp),
      };

      const response = await addVehicle(payload);
      if (response.status === 200 || response.status === 201) {
        setStep(2);
      } else {
        showInfoToast(response.data.message);
      }
    } catch (error) {
      console.error("Add vehicle error", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          color: theme.palette.primary.main,
          fontSize: { xs: "28px", md: "38px" },
          fontWeight: 600,
          mb: 3,
          textAlign: "center",
        }}
      >
        Vehicle's Details
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <Input
            fullWidth
            label="Registration Number"
            name="vehicleRegistrationNumber"
            variant="outlined"
            value={formData.vehicleRegistrationNumber}
            onChange={handleChange}
            error={!!errors.vehicleRegistrationNumber}
            helperText={errors.vehicleRegistrationNumber}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            select
            label="Vehicle Type"
            name="vehicleType"
            value={formData.vehicleType}
            onChange={handleChange}
            error={!!errors.vehicleType}
            helperText={errors.vehicleType}
          >
            <MenuItem value="HEARSE">Hearse</MenuItem>
            <MenuItem value="FAMILY_CAR">Family Car</MenuItem>
            <MenuItem value="UTILITY_VEHICLE">Utitlity Vehicle</MenuItem>
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            fullWidth
            select
            label="Vehicle Color"
            name="vehicleColor"
            value={formData.vehicleColor}
            onChange={handleChange}
          >
            <MenuItem value="">Select color</MenuItem>
            <MenuItem value="WHITE">White</MenuItem>
            <MenuItem value="BLACK">Black</MenuItem>
            <MenuItem value="SILVER">Silver</MenuItem>
            <MenuItem value="GRAY">Gray</MenuItem>
            <MenuItem value="BLUE">Blue</MenuItem>
            <MenuItem value="RED">Red</MenuItem>
            <MenuItem value="BROWN">Brown</MenuItem>
            <MenuItem value="GREEN">Green</MenuItem>
            <MenuItem value="GOLD">Gold</MenuItem>
            <MenuItem value="BEIGE">Beige</MenuItem>
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            fullWidth
            select
            label="Manufacturer"
            name="manufacturerId"
            variant="outlined"
            value={formData.manufacturerId}
            onChange={handleChange}
          >
            <MenuItem value="">Select manufacturer</MenuItem>
            {manufacturer.map((item: any) => (
              <MenuItem
                key={item.manufacturerId}
                value={item.manufacturerId}
              >
                {item.vehicleManufacturerName}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            fullWidth
            select
            label="Vehicle Model"
            name="modelId"
            value={formData.modelId}
            onChange={handleChange}
            disabled={!formData.manufacturerId}
          >
            <MenuItem value="">Select model</MenuItem>
            {models.map((model: any) => (
              <MenuItem key={model.modelId} value={model.modelId}>
                {model.modelName}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            select
            label="Fleet Category"
            name="fleetCategory"
            value={formData.fleetCategory}
            onChange={handleChange}
          >
            <MenuItem value="">Select category</MenuItem>
            <MenuItem value="HOME_TO_HOME">Home-to-Home</MenuItem>
            <MenuItem value="HOME_TO_CEMETERY">Home to Cemetery</MenuItem>
            <MenuItem value="HALL_GROUND_TO_CEMETERY">
              Hall/Ground to Cemetery
            </MenuItem>
            <MenuItem value="CHURCH_TO_HOME">Church to Home</MenuItem>
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            select
            label="Day Delivery"
            name="dayDelivery"
            value={formData.dayDelivery}
            onChange={handleChange}
          >
            <MenuItem value="">Select delivery day</MenuItem>
            <MenuItem value="MORNING_DELIVERY">Morning Delivery</MenuItem>
            <MenuItem value="MONDAY_DELIVERY">Monday Delivery</MenuItem>
            <MenuItem value="TUESDAY_DELIVERY">Tuesday Delivery</MenuItem>
            <MenuItem value="WEDNESDAY_DELIVERY">Wednesday Delivery</MenuItem>
            <MenuItem value="THURSDAY_DELIVERY">Thursday Delivery</MenuItem>
            <MenuItem value="FRIDAY_DELIVERY">Friday Delivery</MenuItem>
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            select
            label="Material Source"
            name="materialSource"
            value={formData.materialSource}
            onChange={handleChange}
          >
            <MenuItem value="">Select source</MenuItem>
            <MenuItem value="BRANCH_STOCK">Branch Stock</MenuItem>
            <MenuItem value="WAREHOUSE">Warehouse</MenuItem>
            <MenuItem value="MORTUARY">Mortuary</MenuItem>
            <MenuItem value="SUPPLIER">Supplier</MenuItem>
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            select
            label="Branch"
            name="branchId"
            value={formData.branchId}
            onChange={handleChange}
            error={!!errors.branchId}
            helperText={errors.branchId}
          >
            <MenuItem value="">Select branch</MenuItem>
            {fleetBranches.map((branch: any) => (
              <MenuItem
                key={branch.id}
                value={branch.id}
              >
                {branch.branchName}
                {branch.province ? ` - ${branch.province}` : ""}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            fullWidth
            type="number"
            label="Current Mileage"
            name="currentMileage"
            value={formData.currentMileage}
            onChange={handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            select
            label="Required Licence Code"
            name="requiredLicenseCode"
            value={formData.requiredLicenseCode}
            onChange={handleChange}
          >
            <MenuItem value="">Not specified</MenuItem>
            <MenuItem value="CODE_8">Code 8</MenuItem>
            <MenuItem value="CODE_10">Code 10</MenuItem>
            <MenuItem value="CODE_14">Code 14</MenuItem>
          </Input>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            select
            label="PDP Required"
            name="requiresPdp"
            value={formData.requiresPdp ? "true" : "false"}
            onChange={(event: any) =>
              setFormData((prev: any) => ({
                ...prev,
                requiresPdp: event.target.value === "true",
              }))
            }
          >
            <MenuItem value="false">No</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
          </Input>
        </Grid>
      </Grid>

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 5,
        }}
      >
        <ButtonComp
          variant="contained"
          height={40}
          width={190}
          onClick={handleSubmit}
        >
          Submit
        </ButtonComp>
      </Box>
    </Box>
  );
}
